import React, { useEffect, useState, useRef } from "react";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider, db } from "../firebase";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import emailjs from '@emailjs/browser';
import logo from "../assets/logo.png";
import { Lock, Loader2, MessageSquare, ShieldCheck, Zap, Activity } from "lucide-react";
import LegalModal from "../components/LegalModal";

emailjs.init("hse-Ri4J-pOKOYoLm");

/* ================= MARKET TICKER COMPONENT ================= */
const MarketTicker = () => {
  const container = useRef();

  useEffect(() => {
    if (container.current.querySelector('script')) return;
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = JSON.stringify({
      "symbols": [
        { "proName": "FOREXCOM:SPX500", "title": "S&P 500" },
        { "proName": "FX_IDC:EURUSD", "title": "EUR/USD" },
        { "proName": "BITSTAMP:BTCUSD", "title": "BTC/USD" },
        { "proName": "OANDA:XAUUSD", "title": "GOLD" },
        { "proName": "OANDA:GBPUSD", "title": "GBP/USD" }
      ],
      "showSymbolLogo": true,
      "colorTheme": "dark",
      "isTransparent": true,
      "displayMode": "adaptive",
      "locale": "en"
    });
    container.current.appendChild(script);
  }, []);

  return (
    <div style={styles.tickerField} ref={container}>
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
};

/* ================= MAIN LOGIN COMPONENT ================= */
export default function Login({ onSupportClick }) {
  const [isLoading, setIsLoading] = useState(false);
  const [legal, setLegal] = useState({ open: false, type: "terms" });

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      html, body { margin: 0; padding: 0; background-color: #000; overflow: hidden; height: 100%; width: 100%; font-family: 'Inter', sans-serif; }
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes pulse { 0% { opacity: 0.3; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.1); } 100% { opacity: 0.3; transform: scale(0.9); } }
    `;
    document.head.append(style);
    return () => style.remove();
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userRef = doc(db, "users", result.user.uid);
      const userSnap = await getDoc(userRef);

      const userData = { 
        uid: result.user.uid, 
        displayName: result.user.displayName, 
        email: result.user.email, 
        lastLogin: new Date().toISOString() 
      };

      if (!userSnap.exists()) {
        userData.createdAt = serverTimestamp();
        userData.plan = "basic";
        emailjs.send('service_v7jisfc', 'template_hr33w8k', { 
          user_name: result.user.displayName, 
          user_email: result.user.email 
        });
      }
      await setDoc(userRef, userData, { merge: true });
    } catch (error) { 
      console.error("Login Error:", error); 
    } finally { 
      setIsLoading(false); 
    }
  };

  return (
    <div style={styles.page}>
      {/* Background Ambience */}
      <div style={styles.radialBackdrop} />
      <div style={styles.gridLayer} />
      
      <div style={styles.statusHeader}>
        <div style={styles.statusDot} />
        <span style={styles.statusText}>SYSTEMS OPERATIONAL</span>
      </div>

      <div style={styles.mainConsole}>
        <div style={styles.topGlowLine} />
        
        <div style={styles.logoArea}>
          <img src={logo} alt="Focused Trader" style={styles.logoImage} />
        </div>

        <p style={styles.mottoText}>DISCIPLINE • CONSISTENCY • PERFORMANCE</p>
        
        <h2 style={styles.heroHeadline}>Trade with <strong>clarity</strong>. Perform with <strong>discipline</strong>.</h2>

        <button 
          disabled={isLoading}
          style={styles.premiumGoogleBtn} 
          onClick={handleGoogleLogin}
        >
          {isLoading ? (
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <>
              <svg width="22" height="22" viewBox="0 0 48 48" style={{ marginRight: 15 }}>
                <path fill="#EA4335" d="M24 9.5c3.15 0 5.96 1.09 8.18 2.87l6.09-6.09C34.64 2.56 29.7 0 24 0 14.62 0 6.48 5.38 2.56 13.22l7.09 5.51C11.43 13.09 17.23 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.1 24.5c0-1.61-.14-2.79-.45-4H24v7.57h12.59c-.25 2.03-1.6 5.08-4.62 7.13l7.09 5.5c4.14-3.83 6.04-9.47 6.04-16.2z" />
                <path fill="#FBBC05" d="M9.65 28.73c-.47-1.4-.73-2.89-.73-4.43s.26-3.03.72-4.43l-7.09-5.51C.92 17.64 0 20.7 0 24.3c0 3.6.92 6.66 2.56 9.94l7.09-5.51z" />
                <path fill="#34A853" d="M24 48c5.7 0 10.64-1.87 14.18-5.09l-7.09-5.5c-1.89 1.31-4.43 2.22-7.09 2.22-6.77 0-12.57-3.59-14.35-8.72l-7.09 5.51C6.48 42.62 14.62 48 24 48z" />
              </svg>
              CONTINUE WITH GOOGLE
            </>
          )}
        </button>

        <p style={styles.subtextLabels}>Secure • No spam • No passwords</p>

        <div style={styles.featureList}>
          <div style={styles.featureItem}><ShieldCheck size={16} color="#22d3ee" /> Encrypted & Secure</div>
          <div style={styles.featureItem}><Zap size={16} color="#22d3ee" /> Built for Prop & Funded Traders</div>
          <div style={styles.featureItem}><Activity size={16} color="#22d3ee" /> Real Performance Analytics</div>
        </div>

        <div style={styles.footerLegal}>
          Access to this terminal implies agreement to <br />
          <span onClick={() => setLegal({ open: true, type: 'terms' })} style={styles.legalLink}>Terms of Service</span> & <span onClick={() => setLegal({ open: true, type: 'privacy' })} style={styles.legalLink}>Privacy Policy</span>
        </div>
        
        <div style={styles.bottomGlowLine} />
      </div>

      {/* FIXED POSITION: Right Corner Above Ticker */}
      <button onClick={onSupportClick} style={styles.fixedSupport}>
        <MessageSquare size={18} style={{ marginRight: 8 }} /> Support
      </button>

      <MarketTicker />
      <LegalModal isOpen={legal.open} type={legal.type} onClose={() => setLegal({ ...legal, open: false })} />
    </div>
  );
}

const styles = {
  page: { height: "100vh", width: "100vw", display: "flex", alignItems: "center", justifyContent: "center", background: "#000", position: "relative", overflow: "hidden" },
  radialBackdrop: { position: "absolute", width: "160vw", height: "160vh", background: "radial-gradient(circle at center, rgba(8, 47, 73, 0.45) 0%, rgba(0, 0, 0, 1) 65%)", zIndex: 1 },
  gridLayer: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(rgba(56, 189, 248, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56, 189, 248, 0.04) 1px, transparent 1px)", backgroundSize: "50px 50px", zIndex: 2 },
  statusHeader: { position: "absolute", top: 40, display: "flex", alignItems: "center", gap: 10, padding: "10px 25px", background: "rgba(2, 6, 23, 0.8)", borderRadius: "100px", border: "1px solid rgba(56, 189, 248, 0.3)", zIndex: 10 },
  statusDot: { width: 8, height: 8, borderRadius: "50%", background: "#22d3ee", boxShadow: "0 0 12px #22d3ee", animation: "pulse 2s infinite" },
  statusText: { color: "#fff", fontSize: "10px", fontWeight: 800, letterSpacing: "2.5px" },
  mainConsole: { width: 480, padding: "80px 40px", borderRadius: "24px", background: "rgba(10, 15, 24, 0.85)", backdropFilter: "blur(60px)", border: "1px solid rgba(56, 189, 248, 0.15)", position: "relative", zIndex: 5, textAlign: "center", boxShadow: "0 40px 100px rgba(0,0,0,0.9)" },
  topGlowLine: { position: "absolute", top: 0, left: "15%", right: "15%", height: "2px", background: "linear-gradient(90deg, transparent, #22d3ee, transparent)", boxShadow: "0 0 15px #22d3ee" },
  bottomGlowLine: { position: "absolute", bottom: 0, left: "15%", right: "15%", height: "2px", background: "linear-gradient(90deg, transparent, #22d3ee, transparent)", boxShadow: "0 0 15px #22d3ee" },
  logoArea: { marginBottom: 30, filter: "drop-shadow(0 0 30px rgba(34, 211, 238, 0.35))" },
  logoImage: { width: 260 },
  mottoText: { fontSize: "10px", color: "#64748b", letterSpacing: "4px", marginBottom: 30 },
  heroHeadline: { color: "#fff", fontSize: "21px", fontWeight: 400, marginBottom: 40, lineHeight: 1.5, letterSpacing: "0.5px" },
  premiumGoogleBtn: { width: "100%", height: 60, borderRadius: "12px", background: "rgba(15, 23, 42, 0.95)", border: "1px solid #22d3ee", color: "#fff", fontSize: "15px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "0.3s", boxShadow: "0 0 25px rgba(34, 211, 238, 0.15)" },
  subtextLabels: { fontSize: "12px", color: "#334155", margin: "20px 0 40px", letterSpacing: "1px" },
  featureList: { display: "flex", flexDirection: "column", gap: 18, marginBottom: 50, alignItems: "center" },
  featureItem: { display: "flex", alignItems: "center", gap: 12, color: "#cbd5e1", fontSize: "14px", fontWeight: 500 },
  footerLegal: { fontSize: "11px", color: "#475569", lineHeight: 1.8 },
  legalLink: { color: "#22d3ee", cursor: "pointer", fontWeight: 700, textDecoration: "underline" },
  fixedSupport: { 
    position: "fixed", 
    bottom: "70px", // Just above the ticker tape
    right: "30px", 
    background: "rgba(15, 23, 42, 0.95)", 
    border: "1px solid rgba(56, 189, 248, 0.4)", 
    color: "#22d3ee", 
    padding: "10px 22px", 
    borderRadius: "12px", 
    display: "flex", 
    alignItems: "center", 
    fontSize: "13px", 
    fontWeight: 700, 
    cursor: "pointer", 
    zIndex: 100,
    boxShadow: "0 8px 20px rgba(0,0,0,0.5)" 
  },
  tickerField: { position: "fixed", bottom: 0, left: 0, width: "100vw", height: "50px", background: "rgba(0,0,0,1)", borderTop: "1px solid rgba(56, 189, 248, 0.2)", zIndex: 100 }
};